import { v4 } from 'uuid';
import {
  ImageUploadValidationError,
  isRawImageFile,
  validateImageDimensions,
  validateImageFileBasics,
} from './imageUploadPolicy';

const JPEG_QUALITY = 0.9;
const DEFAULT_MAX_DIMENSION = 1080;
let standardWorker = null;
let rawWorker = null;
let rawWorkerReady = null;
let processingQueue = Promise.resolve();
const standardRequests = new Map();

function normalizeMaxDimension(value) {
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : DEFAULT_MAX_DIMENSION;
}

function getOutputDimensions(width, height, maxDimension) {
  validateImageDimensions(width, height);
  const limit = normalizeMaxDimension(maxDimension);
  const longestEdge = Math.max(width, height);
  const scale = longestEdge > limit ? limit / longestEdge : 1;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function getProcessedImageFileName(originalFileName) {
  const value = String(originalFileName || '').trim() || 'photo';
  if (/\.jpe?g$/i.test(value)) return value.replace(/\.jpeg$/i, '.jpg');
  // Keep the original extension in the internal upload name. `photo.png.jpg`
  // and `photo.webp.jpg` must not collapse into the same server duplicate key.
  return `${value}.jpg`;
}

function blobToJpegFile(blob, originalFile) {
  return new File([blob], getProcessedImageFileName(originalFile.name), {
    type: 'image/jpeg',
    lastModified: originalFile.lastModified || Date.now(),
  });
}

function getStandardWorker() {
  if (standardWorker) return standardWorker;
  // Keep import.meta in a tiny lazy module so Jest can test the policy helpers
  // without evaluating browser-only Worker URLs.
  const { createImagePreprocessorWorker } = require('./imageWorkerFactory');
  standardWorker = createImagePreprocessorWorker();
  standardWorker.addEventListener('message', (event) => {
    const result = event.data || {};
    const request = standardRequests.get(result.id);
    if (!request) return;
    standardRequests.delete(result.id);
    if (result.status === 'success') request.resolve(result);
    else request.reject(new ImageUploadValidationError('IMAGE_PROCESSING_FAILED', result.message || '图片标准化失败。'));
  });
  standardWorker.addEventListener('error', (event) => {
    const error = new ImageUploadValidationError('IMAGE_PROCESSING_FAILED', event.message || '图片标准化失败。');
    standardRequests.forEach(request => request.reject(error));
    standardRequests.clear();
    standardWorker?.terminate();
    standardWorker = null;
  });
  return standardWorker;
}

function preprocessStandardImage(file, maxDimension) {
  const id = v4();
  return new Promise((resolve, reject) => {
    standardRequests.set(id, { resolve, reject });
    getStandardWorker().postMessage({ id, file, maxDimension: normalizeMaxDimension(maxDimension) });
  });
}

function loadBrowserImage(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new ImageUploadValidationError('INVALID_IMAGE_CONTENT', '无法读取图片，请确认文件格式受支持且文件未损坏。'));
    };
    image.src = objectUrl;
  });
}

async function preprocessStandardImageOnMainThread(file, maxDimension) {
  const image = await loadBrowserImage(file);
  const sourceWidth = image.naturalWidth;
  const sourceHeight = image.naturalHeight;
  const output = getOutputDimensions(sourceWidth, sourceHeight, maxDimension);
  const canvas = document.createElement('canvas');
  canvas.width = output.width;
  canvas.height = output.height;
  const context = canvas.getContext('2d', { alpha: false });
  context.fillStyle = '#fff';
  context.fillRect(0, 0, output.width, output.height);
  context.drawImage(image, 0, 0, output.width, output.height);
  return { blob: await canvasToBlob(canvas), ...output, sourceWidth, sourceHeight };
}

function getRawWorker() {
  if (rawWorkerReady) return rawWorkerReady;
  const { createRawImageWorker } = require('./imageWorkerFactory');
  rawWorker = createRawImageWorker();
  rawWorkerReady = new Promise((resolve, reject) => {
    const onMessage = (event) => {
      if (event.data?.type === 'ready') {
        cleanup();
        resolve(rawWorker);
      } else if (event.data?.type === 'initialization' && event.data?.status === 'error') {
        cleanup();
        reject(new Error(event.data.message || 'RAW 解码器初始化失败'));
      }
    };
    const onError = (event) => {
      cleanup();
      reject(event.error || new Error(event.message || 'RAW 解码器初始化失败'));
    };
    const cleanup = () => {
      rawWorker?.removeEventListener('message', onMessage);
      rawWorker?.removeEventListener('error', onError);
    };
    rawWorker.addEventListener('message', onMessage);
    rawWorker.addEventListener('error', onError);
  }).catch(error => {
    rawWorker?.terminate();
    rawWorker = null;
    rawWorkerReady = null;
    throw error;
  });
  return rawWorkerReady;
}

function canvasToBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (blob) resolve(blob);
      else reject(new Error('JPEG 文件生成失败'));
    }, 'image/jpeg', JPEG_QUALITY);
  });
}

async function rawRgbToJpeg(data, sourceWidth, sourceHeight, maxDimension) {
  validateImageDimensions(sourceWidth, sourceHeight);
  const expectedLength = sourceWidth * sourceHeight * 3;
  if (!data || data.length < expectedLength) throw new Error('RAW 解码结果不完整');

  const rgba = new Uint8ClampedArray(sourceWidth * sourceHeight * 4);
  for (let source = 0, target = 0; source < expectedLength; source += 3, target += 4) {
    rgba[target] = data[source];
    rgba[target + 1] = data[source + 1];
    rgba[target + 2] = data[source + 2];
    rgba[target + 3] = 255;
  }

  const sourceCanvas = document.createElement('canvas');
  sourceCanvas.width = sourceWidth;
  sourceCanvas.height = sourceHeight;
  sourceCanvas.getContext('2d').putImageData(new ImageData(rgba, sourceWidth, sourceHeight), 0, 0);
  const output = getOutputDimensions(sourceWidth, sourceHeight, maxDimension);
  if (output.width === sourceWidth && output.height === sourceHeight) {
    return { blob: await canvasToBlob(sourceCanvas), ...output, sourceWidth, sourceHeight };
  }

  const outputCanvas = document.createElement('canvas');
  outputCanvas.width = output.width;
  outputCanvas.height = output.height;
  const context = outputCanvas.getContext('2d', { alpha: false });
  context.fillStyle = '#fff';
  context.fillRect(0, 0, output.width, output.height);
  context.drawImage(sourceCanvas, 0, 0, output.width, output.height);
  return { blob: await canvasToBlob(outputCanvas), ...output, sourceWidth, sourceHeight };
}

async function preprocessRawImage(file, maxDimension) {
  const worker = await getRawWorker();
  const rawData = await file.arrayBuffer();
  return new Promise((resolve, reject) => {
    const onMessage = async (event) => {
      const result = event.data || {};
      if (result.type === 'imageData') {
        cleanup();
        try {
          resolve(await rawRgbToJpeg(result.data, result.width, result.height, maxDimension));
        } catch (error) {
          reject(error);
        }
      } else if (result.status === 'error') {
        cleanup();
        reject(new Error(result.message || 'RAW 图片解码失败'));
      }
    };
    const onError = (event) => {
      cleanup();
      reject(event.error || new Error(event.message || 'RAW 图片解码失败'));
    };
    const cleanup = () => {
      worker.removeEventListener('message', onMessage);
      worker.removeEventListener('error', onError);
    };
    worker.addEventListener('message', onMessage);
    worker.addEventListener('error', onError);
    worker.postMessage({ rawData, fileName: file.name, type: 'decode' }, [rawData]);
  });
}

async function preprocessImageNow(file, options = {}) {
  validateImageFileBasics(file);
  const maxDimension = normalizeMaxDimension(options.maxDimension);
  let result;
  if (isRawImageFile(file)) {
    result = await preprocessRawImage(file, maxDimension);
  } else {
    try {
      result = await preprocessStandardImage(file, maxDimension);
    } catch (workerError) {
      // Some Safari versions decode HEIC through <img> but not
      // createImageBitmap/OffscreenCanvas in a Worker.
      result = await preprocessStandardImageOnMainThread(file, maxDimension);
    }
  }
  validateImageDimensions(result.sourceWidth, result.sourceHeight);
  validateImageDimensions(result.width, result.height);
  return {
    file: blobToJpegFile(result.blob, file),
    originalFile: file,
    dimensions: { width: result.width, height: result.height },
    sourceDimensions: { width: result.sourceWidth, height: result.sourceHeight },
  };
}

function preprocessImage(file, options = {}) {
  // Keep decoding and canvas allocation strictly serial. A single large RAW can
  // require hundreds of MB while being decoded, so parallel preprocessing is
  // deliberately avoided even if OSS uploads are retried independently.
  const task = processingQueue.then(() => preprocessImageNow(file, options));
  processingQueue = task.catch(() => {});
  return task;
}

export {
  DEFAULT_MAX_DIMENSION,
  JPEG_QUALITY,
  getOutputDimensions,
  getProcessedImageFileName,
  normalizeMaxDimension,
  preprocessImage,
};
